import { FsBeaconWrapper, FsRequestWrapper } from './fs_request';
import type { IClientStore, IIdentityStore } from './iclientstore';
import {
  CurrentScriptPolyfill,
  FsIsMobileAttr,
  FsNamespaceAttr,
  type IFrameLike,
  type IMultiStorage,
  type IWeakMap,
  type SettingsResponse,
  type StoredBundle,
} from '@fullstory/recording-api';

/**
 * Property indicating that a Fullstory agent has loaded in this namespace.
 * Used for isolation - prevents multiple fs.js agents from capturing
 * the same page under the same namespace.
 */
const LOADED_EXPANDO = '_fs_loaded';

/**
 * Global override for the Fullstory namespace.
 * Can be set before loading fs.js to customize the namespace.
 *
 * The preferred way to define the namespace is via the script
 * tag attribute `data-fs-namespace`.
 */
const NS_OVERRIDE_EXPANDO = '_fs_namespace';

// Note: changing the interface declarations here will break existing native
// mobile clients in the field.
export interface Transport {
  init?(orgId: string): boolean;
  // send a message to this frame's parent
  send(message: string, data: string, pageSignature?: string): void;
  // send a message to one of this frame's children
  sendToChild?(frame: IFrameLike, message: string, data: string): void;
}

export type ResolveRejector = (arg?: any) => any;

export type WebViewApiBufferedCall = {
  operation: string;
  arg: any;
  apiSource?: string;
  resolve?: ResolveRejector;
  reject?: ResolveRejector;
};

export interface WebViewAppTransport {
  /** "Message" - sends a message to the mobile SDK */
  m: (message: string) => void;

  /** "Unload" - notifies the mobile SDK of window unload */
  u: (url: string) => void;

  /** "Load" - notifies the mobile SDK of window unload */
  l: (url: string) => void;

  /** "API" - responsible for delegating API calls to the mobile SDK */
  a: (message: string) => void;

  /** holds the buffer of mobile SDK - delegated API calls BEFORE capture starts */
  buffer: WebViewApiBufferedCall[];

  /** flag set by mobile SDK denoting whether capture is allowed */
  allowlisted: boolean | null;
}

// The globals that the Fullstory recording script exposes on 'window'.
export interface Globals {
  [LOADED_EXPANDO]?: string;
  [NS_OVERRIDE_EXPANDO]?: string;

  _fs_ext_debug?: boolean;
  _fs_debug?: boolean;

  _fs_host?: string;
  _fs_app_host?: string;
  _fs_ext_host?: string;
  _fs_rec_host?: string;
  _fs_script?: string;
  _fs_rec_settings_host?: string;

  _fs_ext_org?: string;
  _fs_org?: string;

  _fs_asset_map_id?: string;

  _fs_csp?: any;
  _fs_cookie_domain?: string;

  _fs_ready?: (fail?: boolean) => void;
  _fs_shutdown?: (dangleEmbedId: string) => void;
  _fs_pagestart?: () => void;

  _fs_run_in_iframe?: boolean;
  _fs_is_outer_script?: boolean;

  _fs_replay_flags?: string;

  /** WebView-related globals */
  _fs_transport?: Transport;
  _fs_app_transport?: WebViewAppTransport;
  _fs_app_transport_epoch?: number;
  _fs_drain_api_call_buffer?: (allowlisted: boolean) => void;
  _fs_native_msg_handler?: {
    webShouldCapture: (webViewNamespace: string, url: string) => boolean;
  };
  webkit?: {
    messageHandlers?: {
      _fs_native_msg_handler?: {
        postMessage: (message: Array<any>) => void;
      };
    };
  };

  _fs_use_socket?: boolean;
  _fs_use_mock_protocol?: boolean;

  /** Escape hatch to toggle off the windex slow path - assumes the snapshot is "Clean" */
  _fs_use_polyfilled_apis?: boolean;

  /** Forces fs.js to treat external resources as local ones and try to upload them on the client */
  _fs_force_local_resources?: boolean;

  /** @deprecated March 2023 */
  _fs_capture_on_start?: boolean;
  _fs_capture_on_startup?: boolean;

  /**
   * Replaces the internal HTTP Request API used by the HTTPProtocol. This is used by Wayfinder to send
   * http requests via the background script instead of the content script; this is necessary to avoid
   * requests being visible in the devtools network tab.
   */
  _fs_request?: FsRequestWrapper;

  /**
   * Replaces the internal beacon API used by the HTTPProtocol. This is used by Wayfinder to send
   * beacon requests via the background script instead of the content script; this is necessary to avoid
   * requests being visible in the devtools network tab.
   */
  _fs_beacon?: FsBeaconWrapper;

  /**
   * Prepopulate recording settings to avoid a call to /rec/settings (wayfinder).
   */
  _fs_settings?: SettingsResponse;

  /**
   * Replaces local data storage (wayfinder).
   */
  _fs_clientstore?: IClientStore;
  _fs_identitystore?: IIdentityStore;

  _fs_multistorage?: IMultiStorage<StoredBundle>;

  /**
   * For miscellaneous behavioral changes for wayfinder.
   */
  _fs_is_wayfinder?: boolean;

  /**
   * Used to prioritize the parent element of the windex iframe to be a direct child of the body
   * instead of the documentElement. Customers can turn this on if they want a HTML spec compliant
   * default.
   */
  _fs_windex_iframe?: boolean;

  /**
   * Indicate to fs.js that it should not try to inject into iframes;
   * this is used by wayfinder because it handles injection itself so that we can keep
   * fs.js running in a sandboxed environment as much as possible
   */
  _fs_skip_iframe_injection?: boolean;

  /**
   * Used to prioritize our embedded Promise polyfill over the native Promise.
   */
  _fs_use_polyfilled_promise?: boolean;

  /**
   * Indicate to fs.js that it should check whether HTMLInputElement.prototype.value getter is native
   * and should windex if not
   */
  _fs_clean_value_getter?: boolean;

  /**
   * Indicate to fs.js that it should use this value rather than default 256kb for
   * network budget
   * Note(eugenechang): this number (with LD flag) will control min and max budget
   */
  _fs_network_budget?: number;

  /**
   * Allows orgs to disable the resume feature in fs.js after hibernation.
   */
  _fs_disable_resume?: boolean;

  /**
   * WeakMap for storing "prehooks" for key prototype functions that can
   * be overwritten by other libraries.
   */
  _fs_prehooks?: IWeakMap<object, Record<string, Function>>;

  // reserved
  // - '_fs_use_watched_el_cache' was used to toggle the watched element rule cache, replaced this flag with a heuristic.
}

/**
 * Type representing a Window object with additional Fullstory global properties.
 */
export type GlobalsOn<T extends WindowOrWorkerGlobalScope> = Globals & T;

/**
 * Common type for any window-like global scope with Fullstory properties.
 */
export type GlobalsOnWindowLike = GlobalsOn<WindowOrWorkerGlobalScope>;

/**
 * Enhances a Window object with Fullstory global properties.
 * Used to provide type-safe access to Fullstory-specific globals.
 */
export function getGlobalsOn<Wnd extends WindowOrWorkerGlobalScope>(wnd: Wnd): GlobalsOn<Wnd> {
  return wnd;
}

// The primary namespace we try to use.
const DefaultNS = 'FS';

/**
 * Gets the current Fullstory namespace.
 * Checks for a custom namespace override, falling back to the default 'FS'.
 *
 * @see getFsNamespace for the primary namespace detection method
 */
export function getNamespace(wnd: GlobalsOnWindowLike): string {
  // See if a namespace was manually specified via '_fs_namespace'.
  const globals = getGlobalsOn(wnd);
  return globals[NS_OVERRIDE_EXPANDO] || DefaultNS;
}

/**
 * Checks if Fullstory is already loaded in the given namespace.
 * Used to prevent multiple instances from recording the same page.
 */
export function isLoaded(wnd: GlobalsOnWindowLike, ns: string): boolean {
  try {
    return LOADED_EXPANDO in ((wnd as any)[ns] || {});
  } catch (e) {
    // Ignore errors from cross-origin access attempts
    return false;
  }
}

/**
 * Marks a namespace as loaded.
 * Creates the namespace object if it doesn't exist and sets the loaded flag.
 */
export function setLoaded(wnd: GlobalsOnWindowLike, ns: string): void {
  if (!(ns in wnd)) {
    (wnd as any)[ns] = {};
  }
  try {
    (wnd as any)[ns][LOADED_EXPANDO] = true;
  } catch (e) {
    // Ignore errors from cross-origin access attempts
  }
}

/**
 * Gets the Fullstory namespace using the most accurate method available.
 * First checks the current script tag for a namespace attribute,
 * then falls back to checking global variables.
 */
export function getFsNamespace(wnd: Window): string {
  // global variable namespace
  const globalNamespace = getNamespace(wnd);
  try {
    return getCurrentScript(wnd)?.getAttribute(FsNamespaceAttr) ?? globalNamespace;
  } catch (_) {
    return globalNamespace;
  }
}

export function isScriptFromMobile(wnd: GlobalsOnWindowLike): boolean {
  try {
    return Boolean(getCurrentScript(wnd as Window)?.hasAttribute(FsIsMobileAttr));
  } catch (_) {
    return false;
  }
}

/**
 * Gets the currently executing script element.
 * Handles both standard and polyfilled currentScript implementations.
 */
function getCurrentScript(wnd: Window) {
  const doc = wnd.document;
  return (
    doc.currentScript ??
    // CurrentScriptPolyfill is a polyfill for document.currentScript added by Native SDK
    ((doc as any)[CurrentScriptPolyfill] as typeof document.currentScript)
  );
}
